import { useEffect, useId, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import i18n, { projectLanguageToLng } from "@/zapi-frontend/shared/i18n";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@libs/zapi-ui/ds/components/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@libs/zapi-ui/ds/components/select";
import { Spinner } from "@libs/zapi-ui/ds/components/spinner";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";
import { useFeatureFlag } from "@/zapi-frontend/shared/hooks/useFeatureFlag";
import { isSystemAdmin } from "@/zapi-frontend/shared/utils/permissions";
import { LabsModeSwitch } from "@/zapi-frontend/features/LabsModeSwitch";
import { languageOptions } from "@/zapi-frontend/widgets/CreateProjectForm/const/const";
import { useUserPreferences, TUserLanguage } from "../services/useUserPreferences";
import { useUpdateUserPreferences } from "../services/useUpdateUserPreferences";
import {
  SettingsError,
  SettingsHeader,
  SettingsRow,
  SettingsRowGroup,
  SettingsSection,
  SettingsSectionActions,
  SettingsSectionSkeleton
} from "./blocks/SettingsLayout";

const LABS_SWITCH_ID = "labs-mode";

function LanguageSection() {
  const { t } = useTranslation(["settings", "common"]);
  const { refetch: refetchMe } = useMeStore();
  const queryClient = useQueryClient();
  const { data: preferences, isLoading, isError, refetch } = useUserPreferences();
  const [uiLanguage, setUiLanguage] = useState<TUserLanguage>("english");
  const selectId = useId();

  useEffect(() => {
    if (preferences) setUiLanguage(preferences.uiLanguage);
  }, [preferences]);

  const { mutate: updatePreferences, isPending } = useUpdateUserPreferences({
    onSuccess: async (updated) => {
      queryClient.setQueryData(["userPreferences"], updated);
      await refetchMe?.();
      // The interface switches in an effect that has not run yet, so `t` would still resolve
      // against the outgoing language. Announce the save in the language just chosen.
      const savedT = i18n.getFixedT(projectLanguageToLng(updated.uiLanguage), "settings");
      toast.success(savedT("preferences.saved"));
    },
    onError: (error) => toast.error(error.response?.data?.message ?? t("preferences.saveFailed"))
  });

  const header = (actions?: ReactNode) => <SettingsHeader title={t("tabs.preferences")} actions={actions} />;

  if (isLoading)
    return (
      <>
        {header()}
        <SettingsSectionSkeleton rows={1} />
      </>
    );

  if (isError || !preferences)
    return (
      <>
        {header()}
        <SettingsError
          title={t("redesign.loadFailed")}
          description={t("redesign.loadFailedHint")}
          retryLabel={t("redesign.retry")}
          onRetry={() => void refetch()}
        />
      </>
    );

  const isChanged = uiLanguage !== preferences.uiLanguage;

  const languageLabels = Object.fromEntries(
    languageOptions.map((option) => {
      const name = t(`languageName.${option.value}`);
      return [option.value, name === option.endonym ? name : `${name} (${option.endonym})`];
    })
  );

  return (
    <>
      {header(
        <SettingsSectionActions status={isChanged ? t("redesign.unsaved") : undefined}>
          <Button
            shape="pill"
            data-testid="preferences-save-button"
            onClick={() => updatePreferences({ uiLanguage })}
            disabled={isPending}
          >
            {isPending && <Spinner data-icon="inline-start" aria-label={t("common:loading")} />}
            {t("common.save")}
          </Button>
        </SettingsSectionActions>
      )}
      <SettingsSection title={t("redesign.language.title")}>
        <SettingsRow
          label={t("preferences.uiLanguage")}
          description={t("preferences.uiLanguageHint")}
          htmlFor={selectId}
        >
          <Select
            items={languageLabels}
            value={uiLanguage}
            onValueChange={(value) => value !== null && setUiLanguage(value as TUserLanguage)}
            disabled={isPending}
          >
            <SelectTrigger id={selectId} className="w-full sm:w-56" data-testid="preferences-ui-language-trigger">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {languageOptions.map((option) => (
                <SelectItem
                  key={option.value}
                  value={option.value}
                  data-testid={`preferences-ui-language-${option.value}-item`}
                >
                  {languageLabels[option.value]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </SettingsRow>
      </SettingsSection>
    </>
  );
}

function LabsSection() {
  const { t } = useTranslation(["settings", "common"]);
  const { role } = useMeStore();
  const labsModeAvailable = useFeatureFlag("labs-mode");

  if (!labsModeAvailable && !isSystemAdmin(role)) return null;

  return (
    <SettingsSection title={t("settings:redesign.labs.title")}>
      <SettingsRowGroup>
        <SettingsRow
          label={t("settings:redesign.labs.label")}
          description={t("common:labsMode.description")}
          htmlFor={LABS_SWITCH_ID}
        >
          <LabsModeSwitch variant="inline" showLabel={false} id={LABS_SWITCH_ID} />
        </SettingsRow>
      </SettingsRowGroup>
    </SettingsSection>
  );
}

export function PreferencesSection() {
  return (
    <>
      <LanguageSection />
      <LabsSection />
    </>
  );
}
